import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  PhotoIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useGalleries } from '../../contexts/GalleryContext';
import { uploadImageToStorage } from '../../utils/imageUpload';

// 플레이스홀더 이미지 URL
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop&q=80';

interface GalleryFormData {
  title: string;
  description: string;
  image_url: string;
  thumbnail_url: string;
  is_featured: boolean;
}

const GalleryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addGallery, updateGallery, getGallery } = useGalleries();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<GalleryFormData>({
    title: '',
    description: '',
    image_url: '',
    thumbnail_url: '',
    is_featured: false
  });

  const [errors, setErrors] = useState<Partial<Record<keyof GalleryFormData, string>>>({});
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  // Edit mode: Load existing gallery data
  useEffect(() => {
    if (isEditMode && id) {
      const gallery = getGallery(Number(id));
      if (gallery) {
        setFormData({
          title: gallery.title,
          description: gallery.description || '',
          image_url: gallery.image_url,
          thumbnail_url: gallery.thumbnail_url || '',
          is_featured: gallery.is_featured
        });
        setImagePreview(gallery.thumbnail_url || gallery.image_url);
      }
    }
  }, [isEditMode, id, getGallery]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name as keyof GalleryFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 이미지 파일인지 확인
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    // 파일 크기 체크 (10MB 제한)
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB를 초과할 수 없습니다.');
      return;
    }

    try {
      setUploading(true);

      // Supabase Storage에 이미지 업로드
      const imageUrl = await uploadImageToStorage(file, 'gallery-images');

      // 썸네일도 같은 이미지 사용 (나중에 최적화 가능)
      setFormData({
        ...formData,
        image_url: imageUrl,
        thumbnail_url: imageUrl
      });
      setImagePreview(imageUrl);

      if (errors.image_url) {
        setErrors({ ...errors, image_url: '' });
      }

      console.log('Gallery image uploaded successfully:', imageUrl);
    } catch (error) {
      console.error('Failed to upload gallery image:', error);
      alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    if (window.confirm('이미지를 제거하시겠습니까? 플레이스홀더 이미지가 표시됩니다.')) {
      setFormData({
        ...formData,
        image_url: '',
        thumbnail_url: ''
      });
      setImagePreview('');
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof GalleryFormData, string>> = {};

    if (!formData.title.trim()) newErrors.title = '제목은 필수입니다.';
    // 이미지가 없으면 플레이스홀더를 사용하므로 필수가 아님

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        // 이미지가 없으면 플레이스홀더 사용
        const galleryData = {
          ...formData,
          image_url: formData.image_url || PLACEHOLDER_IMAGE,
          thumbnail_url: formData.thumbnail_url || PLACEHOLDER_IMAGE
        };

        if (isEditMode && id) {
          await updateGallery(Number(id), galleryData);
          alert('갤러리가 성공적으로 수정되었습니다!');
        } else {
          await addGallery(galleryData);
          alert('갤러리가 성공적으로 등록되었습니다!');
        }
        navigate('/admin/gallery');
      } catch (error) {
        console.error('Failed to submit gallery:', error);
        alert('갤러리 저장에 실패했습니다.');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isEditMode ? '갤러리 수정' : '갤러리 등록'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? '갤러리 내용을 수정합니다.' : '새로운 갤러리를 등록합니다.'}
          <span className="text-red-500">*</span> 표시는 필수 항목입니다.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
        {/* 이미지 업로드 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">이미지 업로드</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이미지 <span className="text-gray-500 text-xs">(선택사항 - 없으면 플레이스홀더 표시)</span>
            </label>

            {/* 이미지 미리보기 */}
            <div className="mb-4 relative inline-block">
              <img
                src={imagePreview || PLACEHOLDER_IMAGE}
                alt="Preview"
                className="max-w-md w-full h-64 object-cover rounded-lg border-2 border-gray-300"
              />
              {imagePreview && (
                <button
                  type="button"
                  onClick={handleImageRemove}
                  className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
                  title="이미지 제거"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* 업로드 버튼 */}
            <div className="flex items-center gap-4">
              <label className={`flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                uploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}>
                <PhotoIcon className="h-5 w-5 mr-2" />
                {uploading ? '업로드 중...' : imagePreview ? '이미지 변경' : '이미지 선택'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-500">
                JPG, PNG, GIF 등 (최대 10MB)
              </span>
            </div>
          </div>
        </div>

        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>

          <div className="space-y-6">
            {/* 제목 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="갤러리 제목을 입력하세요"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* 설명 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                설명
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="갤러리에 대한 설명을 입력하세요 (선택사항)"
              />
            </div>

            {/* 메인 노출 */}
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      is_featured: e.target.checked
                    });
                  }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <StarIcon className="h-5 w-5 text-yellow-500" />
                <span className="text-sm font-medium text-gray-700">메인에 노출</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                체크하면 메인 페이지에 이 갤러리가 표시됩니다.
              </p>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/gallery')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={uploading}
            className={`px-6 py-2 rounded-lg text-white ${
              uploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isEditMode ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GalleryForm;
